/**
 * Undo for transform edits: the Move / Rotate / Scale handles (single,
 * multi-selection and selection set), the Super Gizmo, and the Placement
 * page's fields and buttons.
 *
 * Unity's Battlehub handles record their own undo, and the Placement page
 * records through `BeginRecordValue` / `EndRecordValue`
 * (`SelectedGameObjectModel.cs:1545-1606`). In both cases
 * `SelectedGameObjectModel.OnAfterUndoRedo` (`:1398-1407`) then persists the
 * restored transform. Here one edit is one undo step, and its undo and redo
 * WRITE the stored rows, so a restore is persisted and broadcast like the edit
 * itself.
 *
 * The step is recorded only after the edit has been written, with the first
 * `redo` skipped because `record` runs it at once and the edit has already
 * happened. That is `recordImportUndo`'s shape (`ProjectModelUpload.ts`).
 * Recording after the write means an Undo cannot overtake the write it
 * reverses, and a write that fails leaves nothing on the stack.
 *
 * A CARD is not an entity snapshot, but the same handles move it, on its own
 * or as a group member. An edit can therefore carry card poses too
 * (`mediaTiles`), written with `updateMediaTileAsync` like the card's own
 * commits, so a group with a card in it is still ONE step.
 */

import { Ioc } from "@core/ioc/ioc";
import type { EntityTransform, Vec3 } from "@models/data/entityData";
import { mediaTilePose, type MediaTilePose } from "@models/data/mediaTileData";
import { selectionSetPose } from "@models/data/selectionSetData";
import type { SelectionSetPose } from "@models/data/selectionSetTransform";
import { unityEulerToQuat } from "@models/utils/unityBasis";
import type { IProjectModel } from "./IProjectModel";
import { UNDO_REDO, type IUndoRedo } from "./IUndoRedo";

/** One snapshot's transform write: only the channels the edit changed. */
export interface EntityTransformWrite {
  readonly snapshotId: string;
  readonly transform: Partial<EntityTransform>;
}

/** A selection set's own pose row, written after its members. */
export interface SelectionSetPoseWrite {
  readonly selectionSetId: string;
  readonly pose: SelectionSetPose;
}

/** A card's whole pose. Unity persists a card's transform as one row. */
export interface MediaTilePoseWrite {
  readonly tileId: string;
  readonly pose: MediaTilePose;
}

/** Everything one transform edit writes. */
export interface EntityTransformEdit {
  readonly writes: ReadonlyArray<EntityTransformWrite>;
  readonly mediaTiles?: ReadonlyArray<MediaTilePoseWrite>;
  readonly selectionSet?: SelectionSetPoseWrite | null;
}

/**
 * A handle press without a drag, or a Placement field left unchanged, re-derives
 * the stored pose through a conversion round trip that can differ in the last
 * bits. Unity's `Vector3 ==` uses the same tolerance.
 */
const K_TRANSFORM_EPSILON = 1e-5;

/**
 * The stored state `edit` would overwrite: the same snapshots, the same
 * channels, and the set's current pose. Read it BEFORE writing. A gizmo drag
 * moves only the rendered nodes, so the model still holds the pose the drag
 * started from.
 */
export function captureEntityTransformEdit(
  project: IProjectModel,
  sceneId: string,
  edit: EntityTransformEdit,
): EntityTransformEdit {
  const writes: EntityTransformWrite[] = [];
  for (const write of edit.writes) {
    const stored = project.getEntitySnapshotById(sceneId, write.snapshotId)?.transform;
    if (!stored) continue;
    const transform: { -readonly [K in keyof EntityTransform]?: Vec3 } = {};
    if (write.transform.position) transform.position = stored.position;
    if (write.transform.rotation) transform.rotation = stored.rotation;
    if (write.transform.scale) transform.scale = stored.scale;
    writes.push({ snapshotId: write.snapshotId, transform });
  }
  const mediaTiles: MediaTilePoseWrite[] = [];
  for (const write of edit.mediaTiles ?? []) {
    const tile = project.getMediaTile(sceneId, write.tileId);
    if (tile) mediaTiles.push({ tileId: write.tileId, pose: mediaTilePose(tile) });
  }
  const set = edit.selectionSet
    ? project.getSelectionSet(sceneId, edit.selectionSet.selectionSetId)
    : null;
  return {
    writes,
    mediaTiles,
    selectionSet: set ? { selectionSetId: set.ID, pose: selectionSetPose(set) } : null,
  };
}

/**
 * True when `after` changes nothing `before` holds. Rotations are compared as
 * orientations, since a decompose need not return the euler triple that was
 * stored.
 */
export function isSameEntityTransformEdit(
  before: EntityTransformEdit,
  after: EntityTransformEdit,
): boolean {
  const stored = new Map(before.writes.map((write) => [write.snapshotId, write.transform]));
  for (const write of after.writes) {
    const from = stored.get(write.snapshotId);
    if (!from) return false;
    const to = write.transform;
    if (to.position && !(from.position && sameVec3(from.position, to.position))) return false;
    if (to.scale && !(from.scale && sameVec3(from.scale, to.scale))) return false;
    if (to.rotation && !(from.rotation && sameRotation(from.rotation, to.rotation))) return false;
  }
  const storedTiles = new Map((before.mediaTiles ?? []).map((write) => [write.tileId, write.pose]));
  for (const write of after.mediaTiles ?? []) {
    const from = storedTiles.get(write.tileId);
    const to = write.pose;
    if (
      !from
      || !sameVec3(from.position, to.position)
      || Math.abs(from.scale - to.scale) > K_TRANSFORM_EPSILON
      || !sameRotation(from.rotationEuler, to.rotationEuler)
    ) {
      return false;
    }
  }
  if (after.selectionSet) {
    const from = before.selectionSet?.pose;
    const to = after.selectionSet.pose;
    if (
      !from
      || !sameVec3(from.position, to.position)
      || !sameVec3(from.scale, to.scale)
      || !sameRotation(from.rotation, to.rotation)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Write an edit through the model: one snapshot is a single write and several
 * are one batch, as Unity splits `UpdateEntityAsync` / `UpdateEntitiesAsync`.
 * Cards go alongside, one write each, since `UpdateMediaTilesAsync` sends one
 * `update_mediatile` per tile. The set row goes LAST, as every set edit writes
 * it: a failed member write then leaves the set's pose where its members
 * actually are.
 */
export async function applyEntityTransformEdit(
  project: IProjectModel,
  sceneId: string,
  edit: EntityTransformEdit,
): Promise<void> {
  await Promise.all([
    applySnapshotWrites(project, sceneId, edit.writes),
    ...(edit.mediaTiles ?? []).map(({ tileId, pose }) =>
      project.updateMediaTileAsync(sceneId, tileId, pose, ["Transform"])),
  ]);
  if (edit.selectionSet) {
    await project.updateSelectionSetPoseAsync(
      sceneId,
      edit.selectionSet.selectionSetId,
      edit.selectionSet.pose,
    );
  }
}

async function applySnapshotWrites(
  project: IProjectModel,
  sceneId: string,
  writes: ReadonlyArray<EntityTransformWrite>,
): Promise<void> {
  const [only] = writes;
  if (writes.length === 1 && only) {
    await project.updateEntitySnapshotAsync(sceneId, only.snapshotId, { transform: only.transform });
  } else if (writes.length > 1) {
    await project.updateEntitySnapshotsAsync(
      sceneId,
      writes.map(({ snapshotId, transform }) => ({ snapshotId, patch: { transform } })),
    );
  }
}

/**
 * Put an edit that has ALREADY been written on the undo stack as one step.
 * Undo writes `before` and redo writes `after` again. Without a registered
 * undo stack, as in a headless run, this does nothing.
 */
export function recordEntityTransformUndo(
  label: string,
  project: IProjectModel,
  sceneId: string,
  before: EntityTransformEdit,
  after: EntityTransformEdit,
): void {
  if (!Ioc.isRegistered<IUndoRedo>(UNDO_REDO)) return;
  const replay = (edit: EntityTransformEdit, direction: string): void => {
    void applyEntityTransformEdit(project, sceneId, edit).catch((err: unknown) => {
      console.warn(`[EntityTransformUndo] ${direction} "${label}" failed:`, err);
    });
  };
  let firstRedo = true;
  Ioc.resolve<IUndoRedo>(UNDO_REDO).record({
    label,
    redo: () => {
      if (firstRedo) {
        firstRedo = false;
        return; // the edit was written before it was recorded
      }
      replay(after, "redo");
    },
    undo: () => replay(before, "undo"),
  });
}

/**
 * The whole commit for a caller with no write of its own: capture what the
 * edit overwrites, write it, and record it as one undo step. An edit that
 * changes nothing writes nothing and records nothing.
 */
export async function updateEntityTransformsWithUndoAsync(
  project: IProjectModel,
  sceneId: string,
  edit: EntityTransformEdit,
  label: string,
): Promise<void> {
  const before = captureEntityTransformEdit(project, sceneId, edit);
  if (isSameEntityTransformEdit(before, edit)) return;
  await applyEntityTransformEdit(project, sceneId, edit);
  recordEntityTransformUndo(label, project, sceneId, before, edit);
}

/** The undo label for a handle's mode. Labels surface only in logs. */
export function transformUndoLabel(mode: "translate" | "rotate" | "scale" | null): string {
  switch (mode) {
    case "translate":
      return "Move";
    case "rotate":
      return "Rotate";
    case "scale":
      return "Scale";
    default:
      return "Transform";
  }
}

function sameVec3(a: Vec3, b: Vec3): boolean {
  return (
    Math.abs(a[0] - b[0]) <= K_TRANSFORM_EPSILON
    && Math.abs(a[1] - b[1]) <= K_TRANSFORM_EPSILON
    && Math.abs(a[2] - b[2]) <= K_TRANSFORM_EPSILON
  );
}

function sameRotation(a: Vec3, b: Vec3): boolean {
  const qa = unityEulerToQuat(a);
  const qb = unityEulerToQuat(b);
  const dot = qa[0] * qb[0] + qa[1] * qb[1] + qa[2] * qb[2] + qa[3] * qb[3];
  return 1 - Math.abs(dot) <= 1e-9;
}
